// The check sheet (Spencer, 2026-10-12): everything a price rests on, on one page, to read
// and verify. Operations with their times, materials with their prices, then each recipe
// with every step's operation time and every line's material beside it, then the job type's
// route and the standards the items read. Scoped to one way one open estimate is priced (a
// Build, with or without an Add-on), to every way it is priced, to every open estimate priced
// every way, or to the whole library. Tick what has been read and verify it in one tap.
// A row that was verified before says what has changed since, and nothing else, and what changed is lit
// where it is read: the cell (an operation's time, a material's price) or the step or line, with its old
// value beside it, a removed step or line struck through in its place (Spencer, 2026-10-17). What needs
// verifying and has not changed is lit too, in its own colour: a row not yet verified, and inside a recipe or a
// job type each step or line whose operation or material is unverified (Spencer, 2026-10-17: "a good, visible
// highlight for things that need to be verified that have not yet changed", on this sheet only).

import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { changesSinceVerified, type Library as Lib, type Resolution, type VerificationChange, type VerificationChangeRow, type VerificationHistory } from '@zerogap/engine';
import { refresh, rpc, updateRows, useRows, type Row } from '../lib/api';
import { hours, longDate, plural, qty, shortDate } from '../lib/format';
import { jobPricings, jobTitle, pricedAs, useAllJobs } from '../lib/jobs';
import { useSession } from '../lib/session';
import { Btn, Empty, Info, Loading, Panel, Tag } from '../ui/kit';
import { priceWords } from '../ui/pack';
import { TABLES } from './Library';

export type Look = (table: string, id: unknown) => string;

/** What the estimates in scope use, added up across them. Null scope: the whole library. */
interface Use {
  ops: Map<string, { qty: number; minutes: number }>;
  mats: Map<string, { qty: number }>;
  recipes: Map<string, number>;
  types: Set<string>;
  standards: Set<string>;
}

const emptyUse = (): Use => ({ ops: new Map(), mats: new Map(), recipes: new Map(), types: new Set(), standards: new Set() });

/** What one pricing of one job uses. */
function readUse(res: Resolution, typeId: string | null): Use {
  const u = emptyUse();
  if (typeId) u.types.add(typeId);
  for (const i of res.items) for (const s of i.standards_used) u.standards.add(s.key);
  for (const g of res.groups) {
    if (g.recipe_id) u.recipes.set(g.recipe_id, (u.recipes.get(g.recipe_id) ?? 0) + g.item_ids.length);
    for (const s of g.steps) {
      if (!(Number(s.qty) > 0)) continue;
      const was = u.ops.get(s.operation_id) ?? { qty: 0, minutes: 0 };
      u.ops.set(s.operation_id, { qty: was.qty + Number(s.qty), minutes: was.minutes + Number(s.minutes) });
    }
    for (const b of g.bom) {
      if (!b.material_id || !(Number(b.qty) > 0)) continue;
      u.mats.set(b.material_id, { qty: (u.mats.get(b.material_id)?.qty ?? 0) + Number(b.qty) });
    }
  }
  return u;
}

/**
 * Adds one use into another: across jobs the quantities add up; across the ways one job is priced the most any one
 * of them uses counts, so the job's Builds are never counted twice.
 */
function mergeUse(into: Use, from: Use, how: 'sum' | 'most'): void {
  const f = (a: number, b: number) => (how === 'sum' ? a + b : Math.max(a, b));
  for (const [k, v] of from.ops) {
    const was = into.ops.get(k);
    into.ops.set(k, was ? { qty: f(was.qty, v.qty), minutes: f(was.minutes, v.minutes) } : { ...v });
  }
  for (const [k, v] of from.mats) {
    const was = into.mats.get(k);
    into.mats.set(k, was ? { qty: f(was.qty, v.qty) } : { ...v });
  }
  for (const [k, v] of from.recipes) into.recipes.set(k, into.recipes.has(k) ? f(into.recipes.get(k)!, v) : v);
  for (const t of from.types) into.types.add(t);
  for (const s of from.standards) into.standards.add(s);
}

interface Line {
  id: string;
  row: Row;
  cells: ReactNode[];
  /** Why it cannot be verified yet (an operation with no minutes). */
  blocked?: string;
  /** What is read before verifying it: a recipe's steps and lines, a job type's route. */
  body?: ReactNode;
  /** The fields each cell shows, so a cell whose field changed since the verification is lit. */
  fields?: string[][];
}

const dollars = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '';
  const s = String(v);
  if (!/^-?\d*\.?\d+$/.test(s)) return `$${s}`;
  const [whole, frac = ''] = s.split('.');
  const kept = frac.replace(/0+$/, '');
  return `$${whole || '0'}.${kept.length < 2 ? kept.padEnd(2, '0') : kept}`;
};
const words = (key: string) => key.replace(/_(id|expr)$/, '').replace(/_/g, ' ');

// ---------- what changed since it was verified ----------

/** A field's name as the Library shows it, where it has one. */
const FIELD: Record<string, string> = {
  'time_standard.minutes_per_unit': 'minutes per unit',
  'recipe_route_step.qty_expr': 'quantity',
  'recipe_route_step.charge_setup': 'carries setup',
  'recipe_bom_line.qty_expr': 'quantity',
  'recipe_bom_line.unit_price_expr': 'typed price',
  'recipe_bom_line.separate_pricing': 'separate pricing',
  'recipe_bom_line.markup_pct': 'markup %',
  'recipe_bom_line.ignore_margin': 'ignore margin',
  'catalog_derived.expr': 'formula',
  'catalog_input.default_value': 'default',
  'catalog_input.rule_expr': 'rule',
  'catalog_input.hidden_expr': 'hidden when',
  'catalog_child.count_expr': 'how many',
  'catalog_child.child_expr': 'which item',
  'catalog_item.recipe_standard': 'recipe picked by',
  'job_type_route_step.qty': 'a job starts with',
  'job_type_bom_line.qty': 'a job starts with',
  'recipe.unavailable_expr': 'unavailable when',
  'recipe.is_default': 'default recipe',
};
const REF: Record<string, string> = {
  operation_id: 'operation', material_id: 'material', labor_class_id: 'labor_class', phase_id: 'phase', work_center_id: 'work_center',
  vendor_id: 'vendor', alt_vendor_id: 'vendor', child_catalog_item_id: 'catalog_item', catalog_item_id: 'catalog_item',
};

export function fieldName(table: string, field: string): string {
  const known = FIELD[`${table}.${field}`];
  if (known) return known;
  const col = TABLES.find((t) => t.table === table)?.cols.find((c) => c.key === field);
  return col ? col.label.toLowerCase() : words(field);
}

export function shown(field: string, v: unknown, look: Look): string {
  if (v === null || v === undefined || v === '') return 'blank';
  if (REF[field]) return look(REF[field]!, v) || 'one no longer in the library';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
  return s.length > 90 ? `${s.slice(0, 88)}…` : s;
}

/** The row under the verified one that a change is about: "step Edge Banding", "input Width". */
export function rowName(c: VerificationChange, look: Look): string {
  const r = c.row;
  switch (c.table) {
    case 'recipe_route_step':
    case 'job_type_route_step':
      return `step ${look('operation', r.operation_id) || '(operation gone)'}`;
    case 'recipe_bom_line':
    case 'job_type_bom_line':
      return `line ${look('material', r.material_id) || (r.material_role ? `the job's ${words(String(r.material_role))}` : '') || String(r.label ?? 'with no material')}`;
    case 'catalog_part':
      return `part ${r.name ?? ''}`;
    case 'catalog_input':
      return `input ${r.label ?? r.key ?? ''}`;
    case 'catalog_derived':
      return `calculation ${r.label ?? r.key ?? ''}`;
    case 'catalog_child':
      return `child ${r.label ?? look('catalog_item', r.child_catalog_item_id)}`;
    case 'catalog_item':
      return 'the item';
    default:
      return '';
  }
}

/** What changed since each row was last verified, keyed `${table}:${id}` (the change trail, read once and shared). */
export function useVerificationHistories(): Map<string, VerificationHistory> {
  const trail = useQuery({ queryKey: ['verification_changes'], queryFn: () => rpc<VerificationChangeRow[]>('verification_changes') });
  return useMemo(() => changesSinceVerified(trail.data ?? []), [trail.data]);
}

/** A name for a row of any library table a change can point at, so a change reads in words. */
export function useLook(): Look {
  const ops = useRows('operation');
  const mats = useRows('material');
  const cats = useRows('catalog_item');
  const classes = useRows('labor_class');
  const phases = useRows('phase');
  const centers = useRows('work_center');
  const vendors = useRows('vendor');
  const users = useRows('user');
  return useMemo<Look>(() => {
    const names = new Map<string, string>();
    const put = (table: string, rows: Row[], of: (r: Row) => unknown) => {
      for (const r of rows) names.set(`${table}:${r.id}`, String(of(r) ?? ''));
    };
    put('operation', ops.rows, (r) => r.name);
    put('material', mats.rows, (r) => r.description);
    put('catalog_item', cats.rows, (r) => r.name);
    put('labor_class', classes.rows, (r) => r.name);
    put('phase', phases.rows, (r) => r.name);
    put('work_center', centers.rows, (r) => r.name);
    put('vendor', vendors.rows, (r) => r.name);
    put('user', users.rows, (r) => r.name);
    return (table, id) => names.get(`${table}:${String(id)}`) ?? '';
  }, [ops.rows, mats.rows, cats.rows, classes.rows, phases.rows, centers.rows, vendors.rows, users.rows]);
}

/**
 * What changed since this row was verified, for a row that is unverified now. Quiet when it came back without a change of
 * its own (a row under it moved and moved back). `lit` says which changes the screen shows lit on the rows themselves
 * (a recipe's steps and lines): those are pointed at, not listed again.
 */
export function Since({ h, look, compact, lit }: { h: VerificationHistory | undefined; look: Look; compact?: boolean; lit?: (c: VerificationChange) => boolean }) {
  if (!h) return null;
  if (h.changes.length === 0) {
    return <div className={`since quiet${compact ? ' compact' : ''}`}>You verified this on {longDate(h.verified_at)}. Nothing it says has changed since.</div>;
  }
  const below = lit ? h.changes.filter(lit).length : 0;
  const listed = lit ? h.changes.filter((c) => !lit(c)) : h.changes;
  return (
    <div className={`since${compact ? ' compact' : ''}`}>
      <b>Changed since you verified it on {longDate(h.verified_at)}:</b>
      {below > 0 && (
        <>
          {' '}
          {below} {below === 1 ? 'change' : 'changes'} lit below{listed.length > 0 ? ', and' : '.'}
        </>
      )}
      {listed.length > 0 && (
        <ul>
          {listed.map((c, i) => {
            const name = rowName(c, look);
            return (
              <li key={i}>
                {c.kind === 'added' && <>{name} was added</>}
                {c.kind === 'removed' && <>{name} was removed</>}
                {c.kind === 'changed' && (
                  <>
                    {name ? `${name}: ` : ''}
                    {fieldName(c.table, c.field!)} <s>{shown(c.field!, c.was, look)}</s> → <b>{shown(c.field!, c.now, look)}</b>
                  </>
                )}{' '}
                <small>
                  {shortDate(c.at)}
                  {c.by ? ` · ${look('user', c.by)}` : ''}
                </small>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** The changes since the verification that touch one row under it: a step, a line. */
export function changesOn(h: VerificationHistory | undefined, table: string, id: string): VerificationChange[] {
  return h ? h.changes.filter((c) => c.table === table && c.row_id === id) : [];
}

/** What changed on one row since the verification, said on the row, which is lit (`changed`). */
export function ChangeNote({ changes, look }: { changes: VerificationChange[]; look: Look }) {
  if (changes.length === 0) return null;
  const last = changes.reduce((a, c) => (c.at > a.at ? c : a));
  return (
    <div className="changenote">
      {changes.map((c, i) => (
        <Fragment key={i}>
          {i > 0 && ' · '}
          {c.kind === 'added' && <b>added since you verified</b>}
          {c.kind === 'removed' && <b>removed since you verified</b>}
          {c.kind === 'changed' && (
            <>
              {fieldName(c.table, c.field!)} was <s>{shown(c.field!, c.was, look)}</s>
            </>
          )}
        </Fragment>
      ))}{' '}
      <small>
        {shortDate(last.at)}
        {last.by ? ` · ${look('user', last.by)}` : ''}
      </small>
    </div>
  );
}

/** The tables a recipe's or a job type's rows on the sheet light in place; the rest of what changed is listed above them. */
const LIT_HERE = new Set(['recipe_route_step', 'recipe_bom_line', 'job_type_route_step', 'job_type_bom_line']);
const litHere = (c: VerificationChange) => LIT_HERE.has(c.table);

/** Steps or lines removed since the verification, struck through where they were. */
function Removed({ h, table, cols, look }: { h: VerificationHistory | undefined; table: string; cols: number; look: Look }) {
  const gone = h ? h.changes.filter((c) => c.table === table && c.kind === 'removed') : [];
  return (
    <>
      {gone.map((c) => (
        <tr key={c.row_id} className="changed gone">
          <td colSpan={cols}>
            <s>{rowName(c, look).replace(/^(step|line) /, '')}</s>
            {c.row.qty_expr || c.row.qty ? <small> · {String(c.row.qty_expr ?? c.row.qty)}</small> : null}
            <ChangeNote changes={[c]} look={look} />
          </td>
        </tr>
      ))}
    </>
  );
}

// ---------- one section: a table of rows to tick and verify ----------

function Section({ table, title, noun, cols, lines, histories, look, info, empty }: { table: string; title: string; noun: [string, string]; cols: string[]; lines: Line[]; histories: Map<string, VerificationHistory>; look: Look; info: string; empty: string }) {
  const { can, user } = useSession();
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [all, setAll] = useState(false);

  // what came back to be read again first, then what was never verified, then what is verified
  const rank = (l: Line) => (l.row.verified ? 2 : histories.has(`${table}:${l.id}`) ? 0 : 1);
  const sorted = useMemo(() => [...lines].sort((a, b) => rank(a) - rank(b)), [lines, histories]); // eslint-disable-line react-hooks/exhaustive-deps
  const open = sorted.filter((l) => !l.row.verified);
  const tickable = open.filter((l) => !l.blocked);
  const back = open.filter((l) => histories.has(`${table}:${l.id}`)).length;
  const live = [...ticked].filter((id) => tickable.some((l) => l.id === id));
  const rows = all ? sorted : open;

  const toggle = (id: string) => {
    const next = new Set(ticked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setTicked(next);
  };
  const verify = async () => {
    setBusy(true);
    try {
      await updateRows(table, live, { verified: true, verified_by: user?.id ?? null });
      refresh('verification_changes', 'catalog_item');
      setTicked(new Set());
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      soft={false}
      label={
        <>
          {title} · {open.length === 0 ? `all ${lines.length} verified` : `${open.length} of ${lines.length} to check`}
          {back > 0 && <> · <span className="warn">{back} came back</span></>}
        </>
      }
      right={<Info left>{info}</Info>}
    >
      {lines.length === 0 ? (
        <Empty>{empty}</Empty>
      ) : (
        <>
          <div className="sheetbar">
            {can('verify') && tickable.length > 0 && (
              <>
                <Btn small onClick={() => setTicked(live.length === tickable.length ? new Set() : new Set(tickable.map((l) => l.id)))}>{live.length === tickable.length ? 'Untick all' : `Tick all ${tickable.length}`}</Btn>
                <Btn small primary disabled={live.length === 0 || busy} onClick={() => void verify()}>
                  {live.length === 0 ? 'Verify the ticked' : `Verify ${live.length} ${live.length === 1 ? noun[0] : noun[1]}`}
                </Btn>
              </>
            )}
            {lines.length > open.length && (
              <Btn small read onClick={() => setAll(!all)}>
                {all ? 'Only what is left to check' : `Show the ${lines.length - open.length} verified too`}
              </Btn>
            )}
          </div>
          {rows.length === 0 && <Empty>Nothing left to check here.</Empty>}
          {rows.length > 0 && (
            <table className="sheet">
              <thead>
                <tr>
                  <th className="tick" />
                  {cols.map((c) => (
                    <th key={c}>{c}</th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((l) => {
                  const h = l.row.verified ? undefined : histories.get(`${table}:${l.id}`);
                  // the row's own fields that moved (an operation's minutes ride on its time standard)
                  const moved = new Set(h?.changes.filter((c) => c.kind === 'changed' && (c.table === table || c.table === 'time_standard')).map((c) => c.field!) ?? []);
                  return (
                    <Fragment key={l.id}>
                      <tr className={l.row.verified ? 'done' : 'toverify'}>
                        <td className="tick">
                          {!l.row.verified && can('verify') && <input type="checkbox" aria-label={`Tick to verify`} checked={ticked.has(l.id) && !l.blocked} disabled={!!l.blocked} title={l.blocked} onChange={() => toggle(l.id)} />}
                        </td>
                        {l.cells.map((c, i) => (
                          <td key={i} data-label={i === 0 ? undefined : cols[i]} className={l.fields?.[i]?.some((f) => moved.has(f)) ? 'changed' : undefined}>
                            {c}
                          </td>
                        ))}
                        <td className="state">
                          {l.row.verified ? (
                            <span className="nowrap">
                              <Tag kind="on">verified</Tag> <small>{shortDate(l.row.verified_at)}</small>
                            </span>
                          ) : l.blocked ? (
                            <Tag kind="un">{l.blocked}</Tag>
                          ) : h ? (
                            <Tag kind="un">{h.changes.length ? 'changed' : 'came back'}</Tag>
                          ) : (
                            <Tag kind="check">to verify</Tag>
                          )}
                        </td>
                      </tr>
                      {(h || l.body) && (
                        <tr className="under">
                          <td className="tick" />
                          <td colSpan={cols.length + 1}>
                            <Since h={h} look={look} lit={l.body ? litHere : undefined} />
                            {l.body}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}
    </Panel>
  );
}

// ---------- the cells ----------

function opTime(lib: Lib, id: string): ReactNode {
  const o = lib.operations[id];
  if (!o) return '—';
  return o.minutes_per_unit === null ? <span className="warn">no minutes yet</span> : `${qty(o.minutes_per_unit)} min${!o.unit ? '' : o.unit.startsWith('/') ? o.unit : ` per ${o.unit}`}`;
}
function opSetup(lib: Lib, id: string): string {
  const o = lib.operations[id];
  if (!o || !o.setup_minutes || Number(o.setup_minutes) === 0) return '—';
  return `${qty(o.setup_minutes)} min ${o.batch_size ? `per ${qty(o.batch_size)}` : 'per job'}`;
}
function opClass(lib: Lib, id: string): ReactNode {
  const o = lib.operations[id];
  const cls = o?.labor_class_id ? lib.laborClasses[o.labor_class_id] : null;
  if (!cls) return <span className="warn">no labor class</span>;
  const rate = lib.rates[cls.id];
  return `${cls.name} · ${rate ? `${dollars(rate)}/hr` : 'no rate'}`;
}
function matPrice(lib: Lib, id: string | null): ReactNode {
  const m = id ? lib.materials[id] : null;
  if (!m) return '—';
  return m.price === null ? <span className="warn">no price yet</span> : priceWords(m);
}
const Unv = ({ ok }: { ok: boolean | undefined }) => (ok ? null : <Tag kind="check">to verify</Tag>);

/** A recipe as it is read: each step with its operation's time, each line with its material's price; what changed since it was verified (`h`) lit. */
function RecipeBody({ lib, itemId, recipeId, h, look }: { lib: Lib; itemId: string; recipeId: string; h?: VerificationHistory; look: Look }) {
  const recipe = lib.catalog[itemId]?.recipes.find((r) => r.id === recipeId);
  if (!recipe) return null;
  const gone = (table: string) => !!h?.changes.some((c) => c.table === table && c.kind === 'removed');
  return (
    <div className="sheetbody">
      {recipe.route.length === 0 && recipe.bom.length === 0 && <Empty>No steps and no lines: this recipe prices at nothing.</Empty>}
      {(recipe.route.length > 0 || gone('recipe_route_step')) && (
        <table className="sheet sub">
          <thead>
            <tr>
              <th>Step · operation</th>
              <th>How many</th>
              <th>Time</th>
              <th>Setup</th>
              <th>Labor class</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {recipe.route.map((s) => {
              const o = lib.operations[s.operation_id];
              const ch = changesOn(h, 'recipe_route_step', s.id);
              return (
                <tr key={s.id} className={ch.length ? 'changed' : o && !o.verified ? 'toverify' : undefined}>
                  <td>
                    {o ? <Link to={`/library/operation?row=${o.id}`}>{o.name}</Link> : <span className="warn">an operation no longer in the library</span>}
                    {s.note && <small> · {s.note}</small>}
                    <ChangeNote changes={ch} look={look} />
                  </td>
                  <td data-label="How many">
                    <code>{s.qty_expr}</code>
                  </td>
                  <td data-label="Time">{opTime(lib, s.operation_id)}</td>
                  <td data-label="Setup">{s.charge_setup ? opSetup(lib, s.operation_id) : 'none on this step'}</td>
                  <td data-label="Labor class">{opClass(lib, s.operation_id)}</td>
                  <td className="state">
                    <Unv ok={o?.verified} />
                  </td>
                </tr>
              );
            })}
            <Removed h={h} table="recipe_route_step" cols={6} look={look} />
          </tbody>
        </table>
      )}
      {(recipe.bom.length > 0 || gone('recipe_bom_line')) && (
        <table className="sheet sub">
          <thead>
            <tr>
              <th>Line · material</th>
              <th>How many</th>
              <th>Price</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {recipe.bom.map((b) => {
              const m = b.material_id ? lib.materials[b.material_id] : null;
              const ch = changesOn(h, 'recipe_bom_line', b.id);
              return (
                <tr key={b.id} className={ch.length ? 'changed' : m && !m.verified ? 'toverify' : undefined}>
                  <td>
                    {m ? <Link to={`/library/material?row=${m.id}`}>{m.description}</Link> : b.material_role ? `The job's ${words(b.material_role)}` : b.label ?? 'No material chosen'}
                    {b.note && <small> · {b.note}</small>}
                    <ChangeNote changes={ch} look={look} />
                  </td>
                  <td data-label="How many">
                    <code>{b.qty_expr}</code>
                  </td>
                  <td data-label="Price">{m ? matPrice(lib, m.id) : b.unit_price_expr ? <>typed on the job: <code>{b.unit_price_expr}</code></> : b.material_role ? 'the material the job picks' : '—'}</td>
                  <td className="state">{m && <Unv ok={m.verified} />}</td>
                </tr>
              );
            })}
            <Removed h={h} table="recipe_bom_line" cols={4} look={look} />
          </tbody>
        </table>
      )}
      <small>
        {recipe.parts.length > 0 ? `Cut from ${recipe.parts.length} ${recipe.parts.length === 1 ? 'part' : 'parts'}. ` : ''}
        <Link to={`/library/catalog/${itemId}`}>Open in the catalog editor</Link> for the inputs, calculations{recipe.parts.length > 0 ? ', cut list' : ''} and children.
      </small>
    </div>
  );
}

/** A job type's route: each job-level step with what a job starts with and the operation's time. */
function JobTypeBody({ lib, steps, lines, h, look }: { lib: Lib; steps: Row[]; lines: Row[]; h?: VerificationHistory; look: Look }) {
  const gone = (table: string) => !!h?.changes.some((c) => c.table === table && c.kind === 'removed');
  if (steps.length === 0 && lines.length === 0 && !gone('job_type_route_step') && !gone('job_type_bom_line')) return <div className="sheetbody"><Empty>No job-level steps or lines.</Empty></div>;
  return (
    <div className="sheetbody">
      {(steps.length > 0 || gone('job_type_route_step')) && (
        <table className="sheet sub">
          <thead>
            <tr>
              <th>Job step · operation</th>
              <th>A job starts with</th>
              <th>Time</th>
              <th>Labor class</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {steps.map((s) => {
              const o = lib.operations[s.operation_id];
              const start = s.qty === null || s.qty === undefined || s.qty === '' ? null : Number(s.qty);
              const ch = changesOn(h, 'job_type_route_step', s.id);
              return (
                <tr key={s.id} className={ch.length ? 'changed' : o && !o.verified ? 'toverify' : undefined}>
                  <td>
                    {o ? <Link to={`/library/operation?row=${o.id}`}>{o.name}</Link> : 'An operation no longer in the library'}
                    {s.note && <small> · {s.note}</small>}
                    <ChangeNote changes={ch} look={look} />
                  </td>
                  <td data-label="A job starts with">
                    {start === null ? 'blank: typed on each job' : `${qty(start)} ${plural(o?.unit)}`}
                    {start !== null && o?.minutes_per_unit ? <small> · {hours(start * Number(o.minutes_per_unit))}</small> : null}
                  </td>
                  <td data-label="Time">{opTime(lib, s.operation_id)}</td>
                  <td data-label="Labor class">{opClass(lib, s.operation_id)}</td>
                  <td className="state">
                    <Unv ok={o?.verified} />
                  </td>
                </tr>
              );
            })}
            <Removed h={h} table="job_type_route_step" cols={5} look={look} />
          </tbody>
        </table>
      )}
      {(lines.length > 0 || gone('job_type_bom_line')) && (
        <table className="sheet sub">
          <thead>
            <tr>
              <th>Job line · material</th>
              <th>A job starts with</th>
              <th>Price</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lines.map((b) => {
              const m = b.material_id ? lib.materials[b.material_id] : null;
              const ch = changesOn(h, 'job_type_bom_line', b.id);
              return (
                <tr key={b.id} className={ch.length ? 'changed' : m && !m.verified ? 'toverify' : undefined}>
                  <td>
                    {m ? <Link to={`/library/material?row=${m.id}`}>{m.description}</Link> : 'No material chosen'}
                    {b.note && <small> · {b.note}</small>}
                    <ChangeNote changes={ch} look={look} />
                  </td>
                  <td data-label="A job starts with">{b.qty === null || b.qty === undefined || b.qty === '' ? 'blank: typed on each job' : qty(b.qty)}</td>
                  <td data-label="Price">{matPrice(lib, b.material_id ?? null)}</td>
                  <td className="state">{m && <Unv ok={m.verified} />}</td>
                </tr>
              );
            })}
            <Removed h={h} table="job_type_bom_line" cols={4} look={look} />
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------- the sheet ----------

export function CheckSheet() {
  const all = useAllJobs();
  const ops = useRows('operation');
  const mats = useRows('material');
  const cats = useRows('catalog_item');
  const recipes = useRows('recipe');
  const standards = useRows('standard_def');
  const types = useRows('job_type');
  const typeSteps = useRows('job_type_route_step');
  const typeLines = useRows('job_type_bom_line');
  const histories = useVerificationHistories();
  const look = useLook();
  const [params, setParams] = useSearchParams();

  // an open estimate: priced, not locked, not done (lost, not a lead, or closed like a retired duplicate)
  const openJobs = useMemo(() => all.jobs.filter((j) => j.res && !j.job.locked_at && !j.done), [all.jobs]);
  // every way each one is priced: its Builds, alone and with each Add-on (Spencer, 2026-10-16: the laundry room's Cabinotch build)
  const pricings = useMemo(() => new Map(openJobs.map((j) => [j.job.id as string, jobPricings(j)])), [openJobs]);
  // the scope: 'open', 'all', a job's id (every way it is priced), or '<job id>:<pricing key>' (one way)
  const asked = params.get('job');
  const [askedJob, askedWay] = (asked ?? '').split(':');
  const known = !!askedJob && openJobs.some((j) => j.job.id === askedJob) && (!askedWay || !!pricings.get(askedJob)?.some((p) => p.key === askedWay));
  const scope = asked === 'all' ? 'all' : asked && known ? asked : 'open';
  const setScope = (s: string) => {
    const next = new URLSearchParams(params);
    if (s === 'open') next.delete('job');
    else next.set('job', s);
    setParams(next, { replace: true });
  };

  const use = useMemo<Use | null>(() => {
    if (scope === 'all') return null;
    const [scopeJob, scopeWay] = scope.split(':');
    const u = emptyUse();
    for (const j of openJobs) {
      if (scope !== 'open' && j.job.id !== scopeJob) continue;
      const ways = (pricings.get(j.job.id) ?? []).filter((p) => !scopeWay || p.key === scopeWay);
      const mine = emptyUse();
      for (const p of ways) {
        const res = pricedAs(j, p);
        if (res) mergeUse(mine, readUse(res, j.job.job_type_id ?? null), 'most');
      }
      mergeUse(u, mine, 'sum');
    }
    return u;
  }, [openJobs, pricings, scope]);

  const lib = all.lib;
  if (all.loading || ops.loading || mats.loading || recipes.loading || cats.loading || standards.loading || types.loading || typeSteps.loading || typeLines.loading || !lib) return <Loading what="Reading what the estimates rest on" />;

  const inScope = scope === 'all' ? 'in the library' : scope === 'open' ? 'in open estimates' : scope.includes(':') ? 'in this pricing' : 'in this estimate';
  const bySort = (a: Row, b: Row) => (a.sort ?? 0) - (b.sort ?? 0);

  const opLines: Line[] = ops.rows
    .filter((o) => (use ? use.ops.has(o.id) : !o.retired))
    .sort((a, b) => String(a.name).localeCompare(String(b.name)))
    .map((o) => {
      const u = use?.ops.get(o.id);
      const l = lib.operations[o.id];
      return {
        id: o.id,
        row: o,
        blocked: l?.minutes_per_unit === null ? 'no minutes yet' : undefined,
        fields: [['name', 'notes'], ['minutes_per_unit', 'unit'], ['setup_minutes', 'batch_size'], ['labor_class_id']],
        cells: [
          <>
            <Link to={`/library/operation?row=${o.id}`}>{o.name}</Link>
            {o.notes && <small className="note"> {o.notes}</small>}
          </>,
          opTime(lib, o.id),
          opSetup(lib, o.id),
          opClass(lib, o.id),
          ...(use ? [u ? `${qty(u.qty)} ${plural(l?.unit)} · ${hours(u.minutes)}` : '—'] : []),
        ],
      };
    });

  const matLines: Line[] = mats.rows
    .filter((m) => (use ? use.mats.has(m.id) : true))
    .sort((a, b) => String(a.description).localeCompare(String(b.description)))
    .map((m) => ({
      id: m.id,
      row: m,
      blocked: m.price === null || m.price === '' ? 'no price yet' : undefined,
      fields: [['description'], ['price', 'unit', 'pack_size', 'whole_packs'], ['price_updated'], ['vendor_id']],
      cells: [
        <Link to={`/library/material?row=${m.id}`}>{m.description}</Link>,
        matPrice(lib, m.id),
        m.price_updated ? longDate(m.price_updated) : '—',
        look('vendor', m.vendor_id) || '—',
        ...(use ? [`${qty(use.mats.get(m.id)?.qty)} ${m.unit ?? ''}`] : []),
      ],
    }));

  const recipeLines: Line[] = recipes.rows
    .map((r) => ({ r, item: cats.rows.find((c) => c.id === r.catalog_item_id) }))
    .filter(({ r, item }) => item && (use ? use.recipes.has(r.id) : !item.retired))
    .sort((a, b) => String(a.item!.name).localeCompare(String(b.item!.name)) || String(a.r.name).localeCompare(String(b.r.name)))
    .map(({ r, item }) => {
      const lr = lib.catalog[item!.id]?.recipes.find((x) => x.id === r.id);
      return {
        id: r.id,
        row: r,
        cells: [
          <>
            <Link to={`/library/catalog/${item!.id}`}>
              <b>{item!.name}</b>
            </Link>{' '}
            · {r.name} <small>v{r.version}</small>
          </>,
          `${lr?.route.length ?? 0} steps · ${lr?.bom.length ?? 0} lines`,
          ...(use ? [`${use.recipes.get(r.id)} ${use.recipes.get(r.id) === 1 ? 'item' : 'items'}`] : []),
        ],
        body: <RecipeBody lib={lib} itemId={item!.id} recipeId={r.id} h={r.verified ? undefined : histories.get(`recipe:${r.id}`)} look={look} />,
      };
    });

  const typeRows: Line[] = types.rows
    .filter((t) => (use ? use.types.has(t.id) : true))
    .sort((a, b) => String(a.name).localeCompare(String(b.name)))
    .map((t) => {
      const steps = typeSteps.rows.filter((s) => s.job_type_id === t.id).sort(bySort);
      const lines = typeLines.rows.filter((s) => s.job_type_id === t.id).sort(bySort);
      return {
        id: t.id,
        row: t,
        cells: [
          <Link to={`/library/job_type?row=${t.id}`}>
            <b>{t.name}</b>
          </Link>,
          `${steps.length} job steps · ${lines.length} job lines`,
        ],
        body: <JobTypeBody lib={lib} steps={steps} lines={lines} h={t.verified ? undefined : histories.get(`job_type:${t.id}`)} look={look} />,
      };
    });

  const stdLines: Line[] = standards.rows
    .filter((s) => (use ? use.standards.has(s.key) : true))
    .sort(bySort)
    .map((s) => ({
      id: s.id,
      row: s,
      fields: [['label'], ['default_value', 'unit'], ['kind', 'data_type']],
      cells: [
        <Link to={`/library/standard_def?row=${s.id}`}>{s.label}</Link>,
        s.data_type === 'material' ? look('material', s.default_value) || 'picked on the job' : s.data_type === 'catalog_item' ? look('catalog_item', s.default_value) || 'picked on the job' : s.default_value === null || s.default_value === '' ? 'blank' : `${s.default_value}${s.unit ? ` ${s.unit}` : ''}`,
        `${s.kind} · ${String(s.data_type).replace(/_/g, ' ')}`,
      ],
    }));

  const left = [opLines, matLines, recipeLines, typeRows, stdLines].reduce((n, l) => n + l.filter((x) => !x.row.verified).length, 0);
  const total = opLines.length + matLines.length + recipeLines.length + typeRows.length + stdLines.length;

  return (
    <div className="col checksheet">
      <Panel soft={left > 0} label={left === 0 ? `Everything ${inScope} is verified` : `Needs your check · ${left} of ${total} ${inScope}`} right={<Info left>Everything a price rests on, read in the order it builds: an operation's time and a material's price first, then each recipe with those beside its steps and lines. Tick what you have read and verify it in one tap. A name or a note can change without asking you again; anything else brings the row back here, saying what changed.</Info>}>
        <div className="sheetbar">
          <label style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, maxWidth: '100%', minWidth: 0 }}>
            Show what{' '}
            <select className="read" value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="open">every open estimate uses, every build and add-on</option>
              {openJobs.map((j) => {
                const ways = pricings.get(j.job.id) ?? [];
                return (
                  <Fragment key={j.job.id}>
                    <option value={j.job.id}>
                      {jobTitle(j)} uses{ways.length > 1 ? ', every build and add-on' : ''}
                    </option>
                    {ways.length > 1 &&
                      ways.map((p) => (
                        <option key={p.key} value={`${j.job.id}:${p.key}`}>
                          {'\u2003'}
                          {j.job.scope_name}, priced {p.label}
                        </option>
                      ))}
                  </Fragment>
                );
              })}
              <option value="all">the whole library holds</option>
            </select>
          </label>
        </div>
        <small>
          {opLines.length} operations, {matLines.length} materials, {recipeLines.length} recipes, {typeRows.length} job {typeRows.length === 1 ? 'type' : 'types'} and {stdLines.length} standards. A locked job is not listed: it keeps the standards it was locked on; nor is a closed or lost one.
        </small>
      </Panel>

      <Section table="operation" title="Operations" noun={['operation', 'operations']} look={look} histories={histories} lines={opLines}
        cols={['Operation', 'Time', 'Setup', 'Labor class', ...(use ? ['Used'] : [])]}
        empty="No operation is used yet."
        info="The time is minutes for one unit; setup is paid once per job, or once per batch when the operation has a batch size. Verifying an operation covers its minutes: changing them brings it back. An operation with no minutes cannot be verified until it has them." />
      <Section table="material" title="Materials" noun={['material', 'materials']} look={look} histories={histories} lines={matLines}
        cols={['Material', 'Price', 'Price updated', 'Vendor', ...(use ? ['Used'] : [])]}
        empty="No material is used yet."
        info="Only the materials a recipe line or a job line names. A material the job picks through a standard is counted once the job has picked it." />
      <Section table="recipe" title="Recipes" noun={['recipe', 'recipes']} look={look} histories={histories} lines={recipeLines}
        cols={['Catalog item · recipe', 'Holds', ...(use ? ['Used by'] : [])]}
        empty="No catalog item is used yet."
        info="Each recipe with every step's operation time and every line's material beside it. A catalog item reads verified once every recipe under it is. Changing a step, a line, a part, or one of the item's inputs, calculations or children brings the recipe back." />
      <Section table="job_type" title="Job types" noun={['job type', 'job types']} look={look} histories={histories} lines={typeRows}
        cols={['Job type', 'Holds']}
        empty="No job type is used yet."
        info="The job route: the job-level steps and lines every job of the type starts with, and the hours each starts at. Verifying the job type covers them; changing one brings it back." />
      <Section table="standard_def" title="Standards" noun={['standard', 'standards']} look={look} histories={histories} lines={stdLines}
        cols={['Standard', 'Company default', 'Kind']}
        empty="No standard is read yet."
        info="The standards the items read, with the company default a job starts from. What one job set for itself is on that job's setup." />
    </div>
  );
}
